// Account progress sync (Supabase table public.learner_state).
//
// The app keeps working exactly as before on the learner store in localStorage (nell:v1:*);
// this module keeps one account copy of it in the database:
//
//   attach(user)  after sign-in / page load: download the account copy and put it on this
//                 device (or upload this device's copy when that is the newer / only one)
//   (changes)     every learner-store write marks the copy "dirty"; it is uploaded ~1.5 s later
//                 (and when the tab is hidden, and when the browser comes back online)
//   detach()      before sign-out: upload what is left, then remove the learner data from this
//                 device so the next person on this computer starts clean
//
// Which keys: everything in the learner namespace except the session/sync bookkeeping, and
// lab:<unit> states only for units that are not complete (a finished lab's console state is not
// needed to resume, and it is the bulk of the data). Guest (nell:v1:guest:) and admin sandbox
// (nell:v1:admin:) keys are separate namespaces and never synced.
//
// Conflicts (the same account used offline on two devices): the copy with more completed work
// (units + Support Desk tickets) wins; on a tie the account copy wins. The losing local copy is
// kept on the device as nell:v1:local-backup.
//
// PROTOTYPE LIMIT: progress is validated in the browser and the browser uploads the result, so a
// determined user can edit their own progress. RLS guarantees nobody can read or change anyone
// ELSE's progress. Server-side validation is a later phase (README "Accounts and saved progress").
import { SERVER_LAB_DOMAINS, labPayload } from './labVerify.js';

const SKIP = new Set(['session', 'owner', 'sync', 'local-backup', 'remember']);
export const TABLE = 'learner_state';

const completedCount = (snap) => {
  const units = Object.values(snap?.progress?.units || {}).filter((u) => u?.status === 'complete').length;
  const tickets = Object.values(snap?.['support-track']?.tickets || {}).filter((t) => t?.status === 'complete').length;
  const practice = snap?.['support-track']?.practice?.status === 'complete' ? 1 : 0;
  return units + tickets + practice;
};

export function createCloudSync({ client, store, debounceMs = 1500, retryMs = [5000, 15000, 60000], win = typeof window !== 'undefined' ? window : null } = {}) {
  let userId = null; let pulled = false; let applying = false;
  let timer = 0; let pushing = null; let attempt = 0;
  let status = 'off';
  const statusFns = new Set();
  const setStatus = (s) => { if (s !== status) { status = s; for (const f of statusFns) f(s); } };
  const meta = () => store.get('sync') || {};
  const setMeta = (m) => { applying = true; try { store.set('sync', { ...meta(), ...m }); } finally { applying = false; } };

  const syncable = (k) => !SKIP.has(k);
  function snapshot() {
    const out = {};
    const journals = {};
    const prog = store.get('progress');
    for (const k of store.keys()) {
      if (!syncable(k)) continue;
      if (k.startsWith('lab:') && prog?.units?.[k.slice(4)]?.status === 'complete') {
        // a finished lab is not uploaded, but in server-verified domains its journal is (as
        // labj:<unit>, a few KB) so the server can still check it after a reload or on another device
        const v = store.get(k);
        if (SERVER_LAB_DOMAINS.includes(k.slice(4, 7)) && v?.completed === true && Array.isArray(v.journal)) journals[`labj:${k.slice(4)}`] = { ...labPayload(v), completed: true };
        continue;
      }
      const v = store.get(k);
      if (v !== null && v !== undefined) out[k] = v;
    }
    return Object.assign(out, journals); // the device's own finished run wins over an older copy
  }
  function clearLocal() {
    applying = true;
    try { for (const k of store.keys()) if (syncable(k)) store.remove(k); } finally { applying = false; }
  }
  function apply(data) {
    applying = true;
    try {
      for (const k of store.keys()) if (syncable(k)) store.remove(k);
      for (const [k, v] of Object.entries(data || {})) if (syncable(k)) store.set(k, v);
    } finally { applying = false; }
  }
  function backupLocal(snap) {
    if (!Object.keys(snap).length) return;
    applying = true;
    try { store.set('local-backup', { savedAt: new Date().toISOString(), owner: store.get('owner') || null, data: snap }); } finally { applying = false; }
  }

  async function pull() {
    const { data, error } = await client.from(TABLE).select('data, updated_at').eq('user_id', userId).maybeSingle();
    if (error) throw error;
    return data; // null = no row yet
  }

  async function push() {
    if (!userId || !pulled) return false;
    if (pushing) return pushing;
    clearTimeout(timer); timer = 0;
    pushing = (async () => {
      setStatus('saving');
      try {
        const { data, error } = await client.from(TABLE).upsert({ user_id: userId, data: snapshot() }, { onConflict: 'user_id' }).select('updated_at').maybeSingle();
        if (error) throw error;
        setMeta({ userId, dirty: false, syncedAt: data?.updated_at || new Date().toISOString() });
        attempt = 0;
        setStatus(meta().dirty ? 'pending' : 'synced');
        return true;
      } catch {
        setStatus(win && win.navigator && win.navigator.onLine === false ? 'offline' : 'error');
        const wait = retryMs[Math.min(attempt, retryMs.length - 1)]; attempt += 1;
        if (userId && retryMs.length) timer = setTimeout(() => { timer = 0; push(); }, wait);
        return false;
      } finally { pushing = null; }
    })();
    const ok = await pushing;
    if (ok && meta().dirty) schedule(); // changed again while uploading
    return ok;
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(() => { timer = 0; push(); }, debounceMs); setStatus('pending'); }

  const onChange = (key) => {
    if (applying || !userId || !syncable(key)) return;
    setMeta({ userId, dirty: true });
    if (pulled) schedule(); else setStatus('offline');
  };
  const unsubStore = store.onChange(onChange);
  const onOnline = () => { if (userId) (pulled ? (meta().dirty ? push() : null) : api.attach({ id: userId })); };
  const onHide = () => { if (userId && pulled && meta().dirty && win?.document?.visibilityState === 'hidden') push(); };
  win?.addEventListener?.('online', onOnline);
  win?.document?.addEventListener?.('visibilitychange', onHide);

  const api = {
    get status() { return status; },
    get userId() { return userId; },
    onStatus(fn) { statusFns.add(fn); return () => statusFns.delete(fn); },
    snapshot,
    /** Load the account's progress onto this device. Never throws. */
    async attach(user) {
      userId = user.id; pulled = false;
      const owner = store.get('owner');
      const local = snapshot();
      const hasLocal = Object.keys(local).length > 0;
      const m = meta();
      let row;
      try { row = await pull(); } catch {
        // offline: keep this user's own device copy; never show someone else's
        if (owner && owner !== userId) { if (m.dirty) backupLocal(local); clearLocal(); setMeta({ userId, dirty: false, syncedAt: null }); }
        applying = true; try { store.set('owner', userId); } finally { applying = false; }
        setStatus('offline');
        return { ok: false, source: 'device' };
      }
      pulled = true;
      const remote = row?.data && typeof row.data === 'object' ? row.data : null;
      let source = 'account';
      if (owner === userId) {
        const remoteNewer = remote && m.syncedAt && row.updated_at && Date.parse(row.updated_at) > Date.parse(m.syncedAt);
        if (m.dirty && hasLocal && !remote) source = 'device';
        else if (m.dirty && hasLocal && remoteNewer) {
          if (completedCount(local) > completedCount(remote)) source = 'device';
          else backupLocal(local);
        } else if (m.dirty && hasLocal) source = 'device';
        else if (!remote && hasLocal) source = 'device';
      } else if (!owner && hasLocal && !remote) {
        source = 'device'; // first sign-in on this browser: keep the progress made before accounts existed
      } else if (hasLocal && (m.dirty || !owner)) {
        backupLocal(local);
      }
      applying = true; try { store.set('owner', userId); } finally { applying = false; }
      if (source === 'account') { apply(remote || {}); setMeta({ userId, dirty: false, syncedAt: row?.updated_at || null }); setStatus('synced'); }
      else { setMeta({ userId, dirty: true }); await push(); }
      return { ok: true, source };
    },
    /** Upload what is left; then clear this device's learner data (only when the upload worked). */
    async detach({ clear = true } = {}) {
      clearTimeout(timer); timer = 0;
      let ok = true;
      if (userId && meta().dirty) ok = pulled ? await push() : false; // never drop work that is not in the account yet
      if (ok && clear) {
        clearLocal();
        applying = true; try { store.remove('owner'); store.remove('sync'); } finally { applying = false; }
      }
      clearTimeout(timer); timer = 0;
      userId = null; pulled = false; setStatus('off');
      return { ok };
    },
    /** Upload now if anything is waiting (e.g. before leaving). */
    flush: () => (userId && pulled && meta().dirty ? push() : Promise.resolve(true)),
    destroy() { unsubStore(); win?.removeEventListener?.('online', onOnline); win?.document?.removeEventListener?.('visibilitychange', onHide); clearTimeout(timer); },
  };
  return api;
}
